// @ts-strict-ignore
import cx from 'classnames';
import { GetStaticPaths } from 'next';
import { useIntl } from 'react-intl';

import {
  Playlist,
  TopicLandingDataDocument,
  TopicListDocument,
  TopicListItem,
  Video
} from 'api';

import { capitalizeFirstLetter } from 'lib/f';
import getArticlesFromRSS, {
  MAX_ARTICLES_SHOWN
} from 'lib/get-articles-from-rss';
import { initializeApollo } from 'lib/graphql/client';
import { createLogger } from 'lib/logging';

import { LazyLoadAd, TopicPageAds } from 'components/ads';
import ContentContainer from 'components/content-container';
import Divider from 'components/divider';
import Grid from 'components/grid';
import Col from 'components/grid/col';
import { Layout } from 'components/layout';
import PageHeader from 'components/page-header';
import TopicArticles from 'components/pages/topic/slug/articles';
import TopicHead from 'components/pages/topic/slug/head';
import TopicPlaylists from 'components/pages/topic/slug/playlists';
import TopicSidebar from 'components/pages/topic/slug/sidebar';
import TopicTalks from 'components/pages/topic/slug/talks';

import hardcodedTopicsData from './topics.en.json';

const IDEAS_URL = 'https://ideas.ted.com/tag/';

export type Article = {
  title: string;
  link: string;
  thumbnail?: string;
  description: string;
  datePosted: string;
};

type TopicPageProps = {
  articles: Article[];
  articlesTag: string;
  articlesTotalCount: number;
  name: string;
  playlists: Playlist[];
  playlistsTotalCount: number;
  slug: string;
  talks: Video[];
  talksTotalCount: number;
  topicsList: TopicListItem[];
};

const Topic = ({
  articles,
  articlesTag,
  articlesTotalCount,
  name,
  playlists,
  playlistsTotalCount,
  slug,
  talks,
  talksTotalCount,
  topicsList
}: TopicPageProps) => {
  const hasMoreArticles = articlesTotalCount > MAX_ARTICLES_SHOWN;

  const blurb =
    hardcodedTopicsData.topics[slug]?.blurb ||
    hardcodedTopicsData.topics.default.blurb;
  const sidebarData = hardcodedTopicsData.topics[slug]?.sidebar;

  // TODO check if we are keeping %{var} as a translation pattern
  const description = blurb.replace('%{topic_name}', name);
  const subtitleLink = description.includes('TED Member events')
    ? 'https://www.ted.com/membership'
    : null;
  const subTitleLinkText = description.includes('TED Member events')
    ? 'Join TED today to attend live.'
    : null;

  const { formatMessage: t } = useIntl();

  let skipPlaylistsLink: string | null = null;

  if (talks.length > 0) {
    skipPlaylistsLink = '#talks';
  } else if (articles.length > 0) {
    skipPlaylistsLink = '#articles';
  }

  const skipTalksLink = articles.length > 0 ? '#articles' : null;

  const hasContentBelowPlaylists = talks.length > 0 || articles.length > 0;
  const hasContentBelowTalks = articles.length > 0;

  const dividerBelowPlaylistsClasses = cx('my-10 md:my-12', {
    'mt-2 md:mt-4': playlistsTotalCount <= playlists.length
  });

  const dividerBelowTalksClasses = cx('my-10 md:my-12', {
    'mt-2 md:mt-4': talksTotalCount <= talks.length
  });

  const topicRedirect = topicsList?.find(
    topic => topic.slug === slug.toLowerCase()
  )?.name;

  return (
    <Layout>
      <LazyLoadAd showLoader={false}>
        <TopicPageAds.TopicsAdBilllBoardATF />
      </LazyLoadAd>
      <TopicHead
        description={description}
        title={t(
          {
            defaultMessage: 'Ideas about {topicName}',
            id: 'topic.head.title'
          },
          { topicName: name }
        )}
      />
      <div className="w-full bg-white">
        <PageHeader
          leftAligned
          breadcrumbsLinks={[
            {
              label: t({
                defaultMessage: 'Discover',
                id: 'topic.breadcrumbs.discover'
              })
            },
            {
              label: t({
                defaultMessage: 'Topics',
                id: 'topic.breadcrumbs.topics'
              }),
              link: '/topics'
            }
          ]}
          title={name}
          subtitle={description}
          subtitleLink={subtitleLink}
          subTitleLinkText={subTitleLinkText}
        />
        <LazyLoadAd>
          <TopicPageAds.TopicResultsAdInlineATFMobile />
        </LazyLoadAd>
        <ContentContainer className="mb-10 md:mb-12 lg:mb-20">
          <Grid className="mt-10 md:mt-12 xl:mt-20">
            <Col sm={4} lg={8}>
              {playlists.length > 0 && (
                <>
                  <TopicPlaylists
                    name={name}
                    playlists={playlists}
                    totalCount={playlistsTotalCount}
                    skipLink={skipPlaylistsLink || ''}
                    slug={slug}
                  />
                  {hasContentBelowPlaylists && (
                    <Divider className={dividerBelowPlaylistsClasses} />
                  )}
                </>
              )}
              {talks.length > 0 && (
                <>
                  <TopicTalks
                    name={name}
                    slug={encodeURIComponent(topicRedirect || '')}
                    talks={talks}
                    totalCount={talksTotalCount}
                    skipLink={skipTalksLink || ''}
                  />
                  {hasContentBelowTalks && (
                    <Divider className={dividerBelowTalksClasses} />
                  )}
                </>
              )}
              {articles.length > 0 && (
                <TopicArticles
                  articles={articles}
                  name={name}
                  moreLink={
                    hasMoreArticles ? `${IDEAS_URL}${articlesTag}` : undefined
                  }
                />
              )}
            </Col>

            <Col sm={4} lg={4}>
              <Divider className="my-10 md:my-12 lg:hidden" />

              <Grid>
                <Col sm={4} md={6} lg={12} className="lg:flex lg:justify-end">
                  <LazyLoadAd>
                    <TopicPageAds.TopicResultsAdInlineATFDesktop />
                  </LazyLoadAd>
                </Col>
                {sidebarData && (
                  <Col sm={4} md={6} lg={12} className="lg:flex lg:justify-end">
                    <TopicSidebar data={sidebarData} />
                  </Col>
                )}
              </Grid>
            </Col>
          </Grid>
        </ContentContainer>
      </div>
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [
      // Popular topics can be added here to be pre-rendered
      { params: { slug: 'business' } },
      { params: { slug: 'education' } },
      { params: { slug: 'psychology' } },
      { params: { slug: 'art' } },
      { params: { slug: 'communication' } },
      { params: { slug: 'happiness' } },
      { params: { slug: 'animals' } },
      { params: { slug: 'leadership' } },
      { params: { slug: 'health' } },
      { params: { slug: 'technology' } },
      { params: { slug: 'activism' } },
      { params: { slug: 'mental+health' } },
      { params: { slug: 'climate+change' } }
    ],
    fallback: 'blocking'
  };
};

export const getStaticProps = async context => {
  // Name
  const apolloClient = initializeApollo();
  const { data: topicsData, error: topicsError } = await apolloClient.query({
    query: TopicListDocument
  });

  // Logging errors, but data fetching will silently fail and return empty set
  if (topicsError) {
    const log = createLogger('Topics');
    log.error('getting topics list failed', topicsError);
  }

  // sort topic list alphabetically by name
  const topicsList: TopicListItem[] = [...topicsData.topicList.nodes].sort(
    (a: TopicListItem, b: TopicListItem) =>
      (a.name?.toUpperCase() || '') > (b.name?.toUpperCase() || '') ? 1 : -1
  );

  const { data, error } = await apolloClient.query({
    query: TopicLandingDataDocument,
    variables: {
      slug: context.params.slug
    }
  });

  // if the requested slug does not exist as a topic, redirect the request to search
  // instead of presenting an error page
  if (!data.topic?.name) {
    return {
      redirect: {
        destination: `/search?q=${context.params.slug}`
      }
    };
  }

  // Articles are coming from WordPress and tags are using dashes instead of pluses
  const articlesTag = encodeURIComponent(
    // slug is always a string as we are not using catch all for slug in the route definition
    (context.params.slug as string).replace(/\+/g, '-')
  );

  const {
    articles,
    articlesTotalCount,
    error: articlesError
  } = await getArticlesFromRSS(articlesTag);

  // Logging errors, but data fetching will silently fail and return empty sets
  if (error) {
    const log = createLogger('Topic');
    log.error('getting topic data failed', error);
  }

  if (articlesError) {
    const log = createLogger('Topic.ArticlesRSS');
    log.error('getting articles rss failed', articlesError);
  }

  return {
    props: {
      articles,
      articlesTag,
      articlesTotalCount,
      name: capitalizeFirstLetter(data.topic?.name),
      playlists: data.topic?.playlists?.nodes || [],
      playlistsTotalCount: data.topic?.playlists?.totalCount,
      slug: context.params.slug,
      talks: data.topic?.videos?.nodes || [],
      talksTotalCount: data.topic?.videos?.totalCount,
      topicsList
    },
    revalidate: 180
  };
};

export default Topic;
